import Cookies from 'js-cookie';
import { action, computed, makeObservable, observable } from 'mobx';
import { RootStore } from 'core/stores/root-store';
import { FormControl, FormGroup } from 'shared/libs/form';
import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import { CandidateRegisterPayload, CandidateRegisterResponse } from './candidate-register.types';
import { Validators } from 'core/services/Validators';

type CandidateRegisterForm = {
   first_name: FormControl<string>;
   last_name: FormControl<string>;
   middle_name: FormControl<string>;
   email: FormControl<string>;
   contact: FormControl<string>;
   password: FormControl<string>;
   confirm_password: FormControl<string>;
};

type CandidateRegisterSubmitResult =
   | {
        success: true;
        sms: boolean;
        response: CandidateRegisterResponse;
     }
   | {
        success: false;
        status?: number;
        message?: string;
     };

export class CandidateRegisterStore {
   readonly registerState = new AsyncState<CandidateRegisterResponse>();
   showPassword = false;
   showConfirmPassword = false;
   captchaToken: string | null = null;
   captchaError = '';
   submitAttempted = false;
   form!: FormGroup<CandidateRegisterForm>;

   constructor(
      readonly rootStore: RootStore,
      private readonly t: (key: string) => string,
      private readonly language: string
   ) {
      this.form = new FormGroup(
         {
            first_name: new FormControl<string>('', [
               Validators.required,
               Validators.nameValidator,
            ]),
            last_name: new FormControl<string>('', [Validators.required, Validators.nameValidator]),
            middle_name: new FormControl<string>('', [Validators.nameValidator]),
            email: new FormControl<string>('', [Validators.email]),
            contact: new FormControl<string>('', [Validators.contactNumber]),
            password: new FormControl<string>('', [
               (value) => {
                  if (!value) {
                     return this.t('jobseekerRegister.passwordRequired');
                  }

                  if (value.length < 8) {
                     return this.t('jobseekerRegister.passwordLengthError');
                  }

                  return '';
               },
            ]),
            confirm_password: new FormControl<string>('', [
               (value) => {
                  if (!value) {
                     return this.t('jobseekerRegister.confirmPasswordRequired');
                  }

                  if (value.length < 8) {
                     return this.t('jobseekerRegister.passwordLengthError');
                  }

                  return '';
               },
            ]),
         },
         [
            (controls) => {
               const errors: Partial<Record<keyof CandidateRegisterForm, string>> = {};

               if (!controls.email.value.trim() && !controls.contact.value.trim()) {
                  errors.contact = this.t('jobseekerEditProfile.contactOrEmailRequired');
               }

               if (
                  controls.password.value &&
                  controls.confirm_password.value &&
                  controls.password.value !== controls.confirm_password.value
               ) {
                  errors.confirm_password = this.t('jobseekerRegister.passwordMatchError');
               }

               return errors;
            },
         ]
      );

      makeObservable(this, {
         showPassword: observable,
         showConfirmPassword: observable,
         captchaToken: observable,
         captchaError: observable,
         submitAttempted: observable,
         submitting: computed,
         hasActiveSession: computed,
         contactError: computed,
         confirmPasswordError: computed,
         setCaptchaToken: action.bound,
         setCaptchaError: action.bound,
         setContactValue: action.bound,
         togglePasswordVisibility: action.bound,
         toggleConfirmPasswordVisibility: action.bound,
         reset: action.bound,
      });
   }

   get submitting(): boolean {
      return this.registerState.status === 'pending';
   }

   get hasActiveSession(): boolean {
      const tokenClient = Cookies.get('tokenClient');
      const userType = Cookies.get('user_type');

      return Boolean(tokenClient && (userType === 'recruiter' || userType === 'candidate'));
   }

   get contactError(): string {
      const contactControl = this.form.controls.contact;
      const emailControl = this.form.controls.email;

      if (contactControl.error) {
         return contactControl.error;
      }

      if (!this.submitAttempted && !contactControl.dirty && !emailControl.dirty) {
         return '';
      }

      return this.form.error.contact || '';
   }

   get confirmPasswordError(): string {
      const confirmPasswordControl = this.form.controls.confirm_password;
      const passwordControl = this.form.controls.password;

      if (confirmPasswordControl.error) {
         return confirmPasswordControl.error;
      }

      if (!this.submitAttempted && !confirmPasswordControl.dirty && !passwordControl.dirty) {
         return '';
      }

      return this.form.error.confirm_password || '';
   }

   setCaptchaToken(token: string | null): void {
      this.captchaToken = token;

      if (token) {
         this.captchaError = '';
      }
   }

   setCaptchaError(message: string): void {
      this.captchaError = message;
   }

   setContactValue(value: string): void {
      this.form.controls.contact.setValue(value);
   }

   togglePasswordVisibility(): void {
      this.showPassword = !this.showPassword;
   }

   toggleConfirmPasswordVisibility(): void {
      this.showConfirmPassword = !this.showConfirmPassword;
   }

   reset(): void {
      this.form.reset();
      this.submitAttempted = false;
      this.setCaptchaToken(null);
      this.setCaptchaError('');
   }

   private validateForm(): boolean {
      this.submitAttempted = true;
      Object.values(this.form.controls).forEach((control) => control.markAsTouched());
      this.form.validate();

      if (!this.captchaToken) {
         this.captchaError = this.t('jobseekerRegister.captchaRequired');
      } else {
         this.captchaError = '';
      }

      return this.form.valid && !!this.captchaToken;
   }

   private trackRegisterError(message: string): void {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
         event: 'website_error',
         error_type: 'registration error',
         error_message: message,
      });
   }

   private buildPayload(): CandidateRegisterPayload {
      const value = this.form.value;

      return {
         email: value.email.trim(),
         contact: value.contact.trim(),
         password: value.password,
         last_name: value.last_name.trim(),
         first_name: value.first_name.trim(),
         middle_name: value.middle_name.trim(),
         confirm_password: value.confirm_password,
         user_type: 'candidate',
         language: this.language,
      };
   }

   async submit({
      onSuccess,
      onSmsRequired,
      onError,
   }: {
      onSuccess?: (response: CandidateRegisterResponse) => void | Promise<void>;
      onSmsRequired?: (
         contact: string,
         response: CandidateRegisterResponse
      ) => void | Promise<void>;
      onError?: (message: string, status?: number) => void | Promise<void>;
   } = {}): Promise<CandidateRegisterSubmitResult> {
      const valid = this.validateForm();
      if (!valid) {
         return { success: false };
      }

      const payload = this.buildPayload();
      let result: CandidateRegisterSubmitResult = { success: false };

      await this.registerState.run(
         async () => HttpClient.post<CandidateRegisterResponse>('/users/registration', payload),
         {
            onSuccess: async (response) => {
               if (response.status === 200) {
                  if ('sms' in response && response.sms) {
                     result = { success: true, sms: true, response };
                     await onSmsRequired?.(payload.contact, response);
                     return;
                  }

                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                     event: 'registration',
                     authentication_method: 'email',
                     user_email:
                        'response' in response &&
                        typeof response.response === 'object' &&
                        response.response !== null &&
                        'email_address' in response.response
                           ? response.response.email_address
                           : payload.email,
                  });

                  this.setCaptchaToken(null);
                  result = { success: true, sms: false, response };
                  await onSuccess?.(response);
                  return;
               }

               this.trackRegisterError(response.message);
               this.setCaptchaToken(null);
               result = { success: false, status: response.status, message: response.message };
               await onError?.(response.message, response.status);
            },
            onError: async (error) => {
               this.trackRegisterError(error.message);
               this.setCaptchaToken(null);
               result = { success: false, status: error.status, message: error.message };
               await onError?.(error.message, error.status);
            },
         }
      );

      return result;
   }
}
